import { useEffect } from 'react';

import { Mode as ThemeMode, useSnackbar } from '@scaleaq/scaleui';
import { LanguageEnum, ThemeEnum } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { useGetMyUserSettings, useUpdateMyUserSettings } from '@/dataAccess/rest';
import { Languages } from '@/localization';
import { useUserSettingsStore, type UserSettingsState } from '@/stores';

const themeToApi: Record<ThemeMode, ThemeEnum> = {
  dark: ThemeEnum.Dark,
  light: ThemeEnum.Light,
  auto: ThemeEnum.Auto,
};

const themeFromApi: Record<ThemeEnum, ThemeMode> = {
  [ThemeEnum.Dark]: 'dark',
  [ThemeEnum.Light]: 'light',
  [ThemeEnum.Auto]: 'auto',
};

const languageToApi: Record<Languages, LanguageEnum> = {
  [Languages.English]: LanguageEnum.English,
  [Languages.Norwegian]: LanguageEnum.Norwegian,
};

// LanguageEnum.Spanish exists in the API but is not supported in the frontend.
// If a user has Spanish set via API, the fallback keeps the current store language.
const languageFromApi: Partial<Record<LanguageEnum, Languages>> = {
  [LanguageEnum.English]: Languages.English,
  [LanguageEnum.Norwegian]: Languages.Norwegian,
};

/**
 * Fetches user settings from the API and syncs them into the store.
 * Call once in a top-level component (e.g. BaseLayout).
 */
export const useUserSettingsSync = () => {
  const { data: apiSettings, ...rest } = useGetMyUserSettings();

  useEffect(() => {
    if (!apiSettings) return;

    const { language: currentLanguage, syncFromApi } = useUserSettingsStore.getState();
    const mapped: UserSettingsState = {
      theme: themeFromApi[apiSettings.theme] ?? 'auto',
      language: languageFromApi[apiSettings.language] ?? currentLanguage,
      enablePreviewContent: apiSettings.enablePreviewContent ?? false,
    };

    syncFromApi(mapped);
  }, [apiSettings]);

  return { isLoaded: !!apiSettings, ...rest };
};

/**
 * Returns user settings and setters that persist to both the store and the API.
 * Does NOT trigger an API fetch — call useUserSettingsSync once at the top level.
 */
export const useUserSettings = () => {
  const store = useUserSettingsStore();
  const { mutate: updateApi, isPending } = useUpdateMyUserSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const saveAndSync = (next: UserSettingsState) => {
    updateApi(
      {
        theme: themeToApi[next.theme],
        language: languageToApi[next.language],
        enablePreviewContent: next.enablePreviewContent,
      },
      {
        onError: () => {
          enqueueSnackbar(t('errorMessage'), { variant: 'critical' });
        },
      }
    );
  };

  const getCurrent = (): UserSettingsState => ({
    theme: store.theme,
    language: store.language,
    enablePreviewContent: store.enablePreviewContent,
  });

  const setTheme = (theme: ThemeMode) => {
    store.setTheme(theme);
    saveAndSync({ ...getCurrent(), theme });
  };

  const setLanguage = (language: Languages) => {
    store.setLanguage(language);
    saveAndSync({ ...getCurrent(), language });
  };

  const setEnablePreviewContent = (value: boolean) => {
    store.setEnablePreviewContent(value);
    saveAndSync({ ...getCurrent(), enablePreviewContent: value });
  };

  return {
    theme: store.theme,
    language: store.language,
    enablePreviewContent: store.enablePreviewContent,
    isPending,
    setTheme,
    setLanguage,
    setEnablePreviewContent,
  };
};
